import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowUpRight, Check, ChevronLeft } from "lucide-react";
import { Navbar, Footer } from "../components/SiteChrome";
import { fetchOffering, formatUSD } from "../lib/api";

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [offering, setOffering] = useState(null);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchOffering(slug)
      .then((d) => {
        setOffering(d);
        const popular = d.tiers.find((t) => t.popular) || d.tiers[0];
        setSelectedTierId(popular.id);
      })
      .catch(() => setOffering(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const selectedTier = offering?.tiers.find((t) => t.id === selectedTierId);

  const onContinue = () => {
    if (selectedTier) {
      navigate(`/book/${selectedTier.id}`);
    }
  };

  return (
    <div>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <Link
          to="/#services"
          data-testid="back-to-catalog"
          className="inline-flex items-center gap-1 text-sm text-[#5c5a56] hover:text-[#0F1A36] mb-12 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Back to catalog
        </Link>

        {loading && <div className="text-[#5c5a56]">Loading…</div>}
        {!loading && !offering && (
          <div className="text-[#5c5a56]">Offering not found.</div>
        )}
        {!loading && offering && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <div className="overline mb-6">{offering.category}</div>
              <h1 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-none">
                {offering.name}
              </h1>
              <p className="mt-8 text-lg text-[#5c5a56] leading-relaxed">
                {offering.tagline}
              </p>
              <p className="mt-6 text-base text-[#5c5a56] leading-relaxed">
                {offering.summary}
              </p>

              <div className="mt-12 grid grid-cols-2 gap-6 max-w-md">
                <div className="border-t border-[#0F1A36] pt-4">
                  <div className="overline mb-2">Delivery</div>
                  <div className="font-display text-base">{offering.delivery}</div>
                </div>
                <div className="border-t border-[#0F1A36] pt-4">
                  <div className="overline mb-2">Engagement</div>
                  <div className="font-display text-base">
                    {selectedTier?.billing}
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="overline mb-6">Pick a tier</div>
              <div className="space-y-4">
                {offering.tiers.map((tier) => {
                  const active = tier.id === selectedTierId;
                  return (
                    <button
                      key={tier.id}
                      type="button"
                      data-testid={`tier-option-${tier.id}`}
                      onClick={() => setSelectedTierId(tier.id)}
                      className={`w-full text-left p-6 md:p-8 border transition-all ${
                        active
                          ? "border-[#C9A961] bg-white shadow-[0_8px_24px_rgba(201,169,97,0.15)]"
                          : "border-[#d4cdb8] bg-white hover:border-[#0F1A36]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="font-display text-xl font-semibold">
                              {tier.name}
                            </div>
                            {tier.popular && (
                              <span className="text-[10px] uppercase tracking-widest font-semibold bg-[#0F1A36] text-[#f5f1e8] px-2 py-1">
                                Most popular
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-[#5c5a56] mb-4">
                            {tier.summary}
                          </div>
                          <ul className="space-y-1.5 text-sm text-[#0F1A36]">
                            {tier.includes.map((inc) => (
                              <li key={inc} className="flex gap-2 items-start">
                                <Check className="w-4 h-4 text-[#C9A961] mt-0.5 shrink-0" />
                                <span>{inc}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-display text-3xl font-semibold">
                            {formatUSD(tier.price)}
                          </div>
                          <div className="text-xs text-[#5c5a56] mt-1 max-w-[160px] ml-auto">
                            {tier.billing}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={onContinue}
                data-testid="continue-to-booking"
                className="btn-primary mt-10"
              >
                Continue to booking <ArrowUpRight className="w-4 h-4" />
              </button>

              <p className="mt-6 text-xs text-[#5c5a56] max-w-md leading-relaxed">
                The price shown is what you&rsquo;ll be charged today. For
                retainer engagements, the monthly amount begins after kickoff
                and is invoiced separately.
              </p>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
};

export default ServiceDetail;
